class ShegySlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    if (!this.track) return;
    this.prev = this.querySelector('[data-slider-prev]');
    this.next = this.querySelector('[data-slider-next]');
    this.dotsEl = this.querySelector('[data-slider-dots]');
    this.cards = [...this.track.children];
    this._origCount = this.cards.length;

    if (this.prev) this.prev.addEventListener('click', () => { this.advance(-1); this.restartAutoplay(); });
    if (this.next) this.next.addEventListener('click', () => { this.advance(1); this.restartAutoplay(); });
    this.track.addEventListener('scroll', () => this.update(), { passive: true });
    window.addEventListener('resize', () => { this.buildDots(); this.update(); });

    this.initAutoplay();
    this.buildDots();
    requestAnimationFrame(() => this.update());
  }

  /* ---- Autoplay with infinite forward loop ---- */
  initAutoplay() {
    const ms = parseInt(this.dataset.autoplay, 10);
    if (!ms || ms < 500) return;
    this._autoMs = ms;
    this._autoTimer = null;

    // Clone all original cards so the track has double content for seamless wrap
    for (let i = 0; i < this._origCount; i++) {
      const clone = this.cards[i].cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');
      this.track.appendChild(clone);
    }
    this.cards = [...this.track.children];

    this.startAutoplay();
    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => this.startAutoplay());
  }

  startAutoplay() {
    if (!this._autoMs) return;
    this.stopAutoplay();
    this._autoTimer = setInterval(() => {
      const w = this.cardWidth();
      if (!w) return;
      // If scrolled into clone territory, instantly jump back to start
      const resetAt = w * this._origCount;
      if (this.track.scrollLeft >= resetAt - 2) {
        this.track.style.scrollBehavior = 'auto';
        this.track.scrollLeft = 0;
        void this.track.offsetHeight;
        this.track.style.scrollBehavior = '';
      }
      this.advance(1);
    }, this._autoMs);
  }

  stopAutoplay() {
    if (this._autoTimer) { clearInterval(this._autoTimer); this._autoTimer = null; }
  }

  restartAutoplay() {
    if (this._autoMs) this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  /* ---- Core slider helpers ---- */
  cardWidth() {
    const a = this.cards[0], b = this.cards[1];
    if (!a) return 0;
    const gap = b ? (b.offsetLeft - (a.offsetLeft + a.offsetWidth)) : 0;
    return a.offsetWidth + Math.max(0, gap);
  }

  visibleCount() {
    const w = this.cardWidth();
    if (!w) return 1;
    return Math.max(1, Math.round(this.track.clientWidth / w));
  }

  totalSteps() {
    return Math.max(1, this._origCount - this.visibleCount() + 1);
  }

  currentStep() {
    const w = this.cardWidth();
    if (!w) return 0;
    return Math.round(this.track.scrollLeft / w);
  }

  advance(dir) {
    const w = this.cardWidth();
    if (!w) return;
    this.track.scrollBy({ left: dir * w, behavior: 'smooth' });
  }

  buildDots() {
    if (!this.dotsEl) return;
    this.dotsEl.innerHTML = '';
    const n = this.totalSteps();
    for (let i = 0; i < n; i++) {
      const b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('aria-label', `Slide ${i + 1}`);
      b.addEventListener('click', () => {
        this.track.scrollTo({ left: i * this.cardWidth(), behavior: 'smooth' });
        this.restartAutoplay();
      });
      this.dotsEl.appendChild(b);
    }
  }

  update() {
    const idx = this.currentStep() % this._origCount;
    const total = this.totalSteps();
    if (this.dotsEl) {
      [...this.dotsEl.children].forEach((d, i) => d.classList.toggle('is-active', i === Math.min(idx, total - 1)));
    }
    if (!this._autoMs) {
      if (this.prev) this.prev.toggleAttribute('disabled', this.currentStep() <= 0);
      if (this.next) this.next.toggleAttribute('disabled', this.currentStep() >= total - 1);
    }
    if (this._origCount <= this.visibleCount()) {
      this.setAttribute('data-no-slider', '');
    } else {
      this.removeAttribute('data-no-slider');
    }
  }
}

if (!customElements.get('shegy-slider')) {
  customElements.define('shegy-slider', ShegySlider);
}
